import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AbstractContentComponent } from '../../abstract-content.component';
import { Context } from '@service/context.service';
import { MappedValue } from '@entity/catalog';
import { TreeNode } from 'primeng/api';
import { Column } from '@shared/table/column';
import { MappingCatalogDetailDialogComponent } from './mapping-catalog-detail-dialog/mapping-catalog-detail-dialog.component';
import { MappingCatalogEpicExportDialogComponent } from './mapping-catalog-epic-export-dialog/mapping-catalog-epic-export-dialog.component';
import { EpicExportConfig } from '@entity/epic-export-config';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TABLE_SERVICE } from '@shared/table/table-service';
import { MappingCatalogDetailTableService } from './mapping-catalog-detail-table.service';
import { downloadBlob } from '../../download';
import { CatalogService } from '@service/catalog.service';
import { MainDataService } from '@service/main-data.service';
import { ActionButtonDirective } from '@shared/table/action-button.directive';

import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';
import { TreeTableComponent } from '@shared/table/tree-table.component';
import { AuthorizationService } from '@service/authorization.service';

@Component({
  selector: 'app-mapping-catalog-detail',
  templateUrl: './mapping-catalog-detail.component.html',
  styleUrls: ['./mapping-catalog-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TABLE_SERVICE,
      useClass: MappingCatalogDetailTableService,
    },
  ],
  imports: [TreeTableComponent, ToolbarButtonDirective, ActionButtonDirective],
})
export class MappingCatalogDetailComponent extends AbstractContentComponent implements OnInit {
  private mappingCatalogDetailTableService = inject<MappingCatalogDetailTableService>(TABLE_SERVICE);
  private catalogService = inject(CatalogService);
  private mainDataService = inject(MainDataService);
  private authService = inject(AuthorizationService);

  value: TreeNode[] = [];
  columns: Column[] = [];
  additionalColumns: MappedValue[] = [];
  isCreateAllowed = false;
  hierarchyLevels = (maxDepth: number): { label: string; level: number }[] => {
    const levels = [{ label: this.translate('MAPPING_CATALOGS.GROUP'), level: 0 }];
    for (let level = 1; level < maxDepth; level++) {
      levels.push({ label: this.translate('MAPPING_CATALOGS.SUBGROUP', { level }), level });
    }
    levels.push({ label: this.translate('MAPPING_CATALOGS.DETAIL'), level: maxDepth });
    return levels;
  };
  private tacsVariables?: MappedValue[];

  constructor() {
    super(Context.Global);
    this.setBreadcrumbs([
      {
        label: this.translate('MENU.MAPPING_CATALOGS'),
        routerLink: [
          this.activatedRoute.snapshot.data.isMandantSection ? 'mapping-catalogs' : 'admin-mapping-catalogs',
        ],
      },
      { label: this.translate('GLOBAL.DETAILS') },
    ]);
  }

  ngOnInit(): void {
    this.isCreateAllowed = !this.activatedRoute.snapshot.data.isMandantSection;
    this.setColumns();

    const action =
      this.activatedRoute.snapshot.queryParams.type === 'Measure'
        ? this.activatedRoute.snapshot.data.isMandantSection
          ? this.catalogService.getMeasureMappings(this.getMandantId(), this.activatedRoute.snapshot.params.id)
          : this.mainDataService.getMeasureMappings(this.activatedRoute.snapshot.params.id)
        : this.activatedRoute.snapshot.data.isMandantSection
          ? this.catalogService.getVariableMappings(this.getMandantId(), this.activatedRoute.snapshot.params.id)
          : this.mainDataService.getVariableMappings(this.activatedRoute.snapshot.params.id);

    action.subscribe((result) => {
      this.tacsVariables = result;
    });
  }

  showColumnSelection(): void {
    this.showDetail(undefined, this.additionalColumns)?.onClose.subscribe((result) => {
      if (result) {
        this.additionalColumns = result;
        this.setColumns();
        this.markForCheck();
      }
    });
  }

  showDetailForRow(row: TreeNode): void {
    this.showDetail(row)?.onClose.subscribe((result) => {
      if (result) {
        this.mappingCatalogDetailTableService.reload();
      }
    });
  }

  showDetail(row?: TreeNode, selectedColumns?: MappedValue[]): DynamicDialogRef | null {
    return this.dialogService.open(MappingCatalogDetailDialogComponent, {
      modal: true,
      closable: true,
      focusOnShow: false,
      height: 'calc(100vh - 100px)',
      header: `${this.translate('MAPPING_CATALOGS.' + this.activatedRoute.snapshot.queryParams.type.toUpperCase())} - ${row?.data?.id} ${row?.data?.name}`,
      contentStyle: {
        width: '700px',
        overflow: 'hidden',
        display: 'flex',
        'flex-direction': 'column',
      },
      data: {
        mandantId: this.activatedRoute.snapshot.data.isMandantSection ? this.getMandantId() : null,
        catalogId: this.activatedRoute.snapshot.params.id,
        mappingType: this.activatedRoute.snapshot.queryParams.type,
        detailId: row?.data?.dbId,
        tacsVariables: structuredClone(this.tacsVariables),
        selectedColumns,
      },
    });
  }

  setActive(row: TreeNode, active: boolean): void {
    this.showLoadingDialog();
    this.catalogService
      .setCatalogDetailActive(this.getMandantId(), this.activatedRoute.snapshot.params.id, row.data?.dbId, active)
      .subscribe({
        next: () => {
          this.hideLoadingDialog();
          this.showSuccessMessage();
          this.mappingCatalogDetailTableService.reload();
        },
        error: () => this.showErrorMessage(),
      });
  }

  resetToDefault(): void {
    this.confirm({
      header: this.translate('MAPPING_CATALOGS.RESET_TO_DEFAULT'),
      message: this.translate('MAPPING_CATALOGS.RESET_TO_DEFAULT_MESSAGE'),
      acceptLabel: this.translate('GLOBAL.YES'),
      rejectLabel: this.translate('GLOBAL.NO'),
      accept: () => {
        this.showLoadingDialog();
        this.catalogService.resetCatalogMapping(this.getMandantId(), this.activatedRoute.snapshot.params.id).subscribe({
          next: () => {
            this.hideLoadingDialog();
            this.showSuccessMessage();
            this.mappingCatalogDetailTableService.reload();
          },
          error: () => this.showErrorMessage(),
        });
      },
    });
  }

  downloadMappingCatalog(): void {
    const action = this.isCreateAllowed
      ? this.mainDataService.exportMappingCatalog(this.activatedRoute.snapshot.params.id)
      : this.catalogService.exportMapping(this.getMandantId(), this.activatedRoute.snapshot.params.id);
    this.runCatalogDownload(action, 'mapping.xlsx');
  }

  private runCatalogDownload(action: Observable<HttpResponse<Blob>>, filename: string): void {
    this.showLoadingDialog();
    action.subscribe({
      next: (result) => {
        this.hideLoadingDialog();
        if (result.body) {
          downloadBlob(result.body, filename);
          this.showSuccessMessage(this.translate('MAPPING_CATALOGS.DOWNLOAD_SUCCESS'));
        } else {
          // A 200 with no body produced nothing to download; tell the user instead of failing silently.
          this.showErrorMessage(this.translate('GLOBAL.DOWNLOAD_FAILED'));
        }
      },
      error: (err) => this.handleDownloadError(err),
    });
  }

  private handleDownloadError(err: unknown): void {
    this.hideLoadingDialog();
    // The download uses responseType:'blob', so a 400 ValidationException body arrives as a Blob the
    // default handler can't read. Only that case needs the async decode; every other error stays on
    // the synchronous generic path.
    if (err instanceof HttpErrorResponse && err.status === 400 && err.error instanceof Blob) {
      void this.showBlobValidationError(err.error);
      return;
    }
    this.showErrorMessage();
  }

  private async showBlobValidationError(body: Blob): Promise<void> {
    try {
      const parsed = JSON.parse(await body.text());
      // ASP.NET [ApiController] model validation returns ValidationProblemDetails ({ errors, title,
      // status }) — a different shape than the custom ValidationException handled above. Surface a
      // config-specific message rather than the generic one (the dialog's own validators normally
      // prevent this, so it is defence in depth).
      if (parsed?.errors && typeof parsed.errors === 'object') {
        this.showErrorMessage(this.translate('MAPPING_CATALOGS.EPIC_EXPORT_INVALID_CONFIG'));
        return;
      }
    } catch {
      // Body wasn't a JSON ValidationException — fall through to the generic message.
    }
    this.showErrorMessage();
  }

  openEpicExportConfig(): void {
    this.dialogService
      .open(MappingCatalogEpicExportDialogComponent, {
        modal: true,
        closable: true,
        focusOnShow: true,
        header: this.translate('MAPPING_CATALOGS.DOWNLOAD_EPIC'),
        contentStyle: { width: '500px' },
      })
      ?.onClose.subscribe((config: EpicExportConfig | undefined) => {
        if (!config) {
          return;
        }
        this.exportEpicMappingCatalog(config);
      });
  }

  exportEpicMappingCatalog(config: EpicExportConfig): void {
    const action = this.isCreateAllowed
      ? this.mainDataService.exportEpicMappingCatalog(this.activatedRoute.snapshot.params.id, config)
      : this.catalogService.exportEpicMappingCatalog(
          this.getMandantId(),
          this.activatedRoute.snapshot.params.id,
          config
        );
    this.runCatalogDownload(action, 'mapping-epic.zip');
  }

  private setColumns(): void {
    this.columns = [
      {
        headerKey: 'GLOBAL.ID',
        field: 'id',
        minWidth: '200px',
        editable: this.isCreateAllowed,
      },
      {
        headerKey: 'GLOBAL.NAME',
        field: 'name',
        minWidth: '200px',
        editable: this.isCreateAllowed,
      },
    ];

    this.additionalColumns.forEach((c) => {
      this.columns.push({
        header: c.tacsId + ' ' + c.name,
        field: 'mappedValues.' + c.tacsId.replace(/\./g, '_'),
        minWidth: '200px',
        filterType: 'boolean',
        fastEdit: true,
        editable: true,
      });
    });
  }
}
